import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EQUIPMENT_MAINTENANCE_INTERVAL_MAX,
  EQUIPMENT_MAINTENANCE_TITLE_MAX,
  type IEquipmentMaintenanceDraft,
  type EquipmentMaintenanceUnit,
} from '@d-orca/shared/equipment-maintenance';
import { Modal } from '@components/ui/Modal/Modal';
import { Button } from '@components/ui/Button/Button';
import { Field } from '@components/ui/Field/Field';
import { DateField } from '@components/ui/DateField/DateField';
import { dateToYmd, ymdToDate } from '@components/ui/DateField/fieldDate';
import { PickCombo } from '@components/ui/PickCombo/PickCombo';
import styles from './MaintenanceDialog.module.css';

type RepeatChoice = 'none' | EquipmentMaintenanceUnit;

interface IMaintenanceDialogProps {
  initial: IEquipmentMaintenanceDraft | null; // `null` adds a new deadline; a draft edits that one.
  today: string;
  onClose: () => void;
  onSave: (draft: IEquipmentMaintenanceDraft) => void;
  saving?: boolean;
}

export function MaintenanceDialog({ initial, today, onClose, onSave, saving = false }: IMaintenanceDialogProps) {
  const { t } = useTranslation();
  const repeatLabelId = useId();
  const [title, setTitle] = useState(initial?.title ?? '');
  const [dueDate, setDueDate] = useState(initial?.firstDueDate ?? today);
  const [repeat, setRepeat] = useState<RepeatChoice>(initial?.recurrence?.unit ?? 'none');
  const [every, setEvery] = useState(String(initial?.recurrence?.every ?? 1));
  // An edit renames only: the date and the repeat show, locked.
  const locked = initial !== null;
  // A repeating deadline's date is where the repeat starts.
  const dateLabel = t(`equipment.detail.maintenance.dialog.${repeat === 'none' ? 'dueDate' : 'firstDueDate'}`);

  const interval = Number(every);
  const intervalValid = Number.isInteger(interval) && interval >= 1 && interval <= EQUIPMENT_MAINTENANCE_INTERVAL_MAX;
  const canSave = title.trim() !== '' && (repeat === 'none' || intervalValid);

  const save = () =>
    onSave({
      title: title.trim(),
      firstDueDate: dueDate,
      recurrence: repeat === 'none' ? null : { every: interval, unit: repeat },
    });

  const repeatOptions = (['none', 'day', 'week', 'month'] as const).map((value) => ({
    value,
    label: t(`equipment.detail.maintenance.dialog.repeatOption.${value}`),
  }));

  return (
    <Modal
      open
      onClose={onClose}
      title={t(initial ? 'equipment.detail.maintenance.dialog.editTitle' : 'equipment.detail.maintenance.dialog.addTitle')}
      footer={
        <>
          <span className={styles.spacer} />
          <Button variant="secondary" size="md" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" size="md" disabled={!canSave || saving} onClick={save}>
            {t(initial ? 'common.save' : 'equipment.detail.maintenance.add')}
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        <Field
          label={t('equipment.detail.maintenance.dialog.title')}
          requiredMark
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t('equipment.detail.maintenance.dialog.titlePlaceholder')}
          maxLength={EQUIPMENT_MAINTENANCE_TITLE_MAX}
        />
        <div className={styles.field}>
          <span className={styles.label}>
            {dateLabel}
            <span className={styles.required} aria-hidden="true">*</span>
          </span>
          <DateField
            value={ymdToDate(dueDate)}
            onChange={(next) => setDueDate(dateToYmd(next))}
            placeholder={t('equipment.detail.maintenance.dialog.dueDatePlaceholder')}
            ariaLabel={dateLabel}
            today={ymdToDate(today)}
            disabled={locked}
            elevated
          />
        </div>
        <div className={styles.repeatRow}>
          <div className={styles.field}>
            <span className={styles.label} id={repeatLabelId}>{t('equipment.detail.maintenance.dialog.repeat')}</span>
            <PickCombo
              value={repeat}
              options={repeatOptions}
              placeholder={t('equipment.detail.maintenance.dialog.repeatOption.none')}
              onPick={(value) => setRepeat(value as RepeatChoice)}
              ariaLabelledBy={repeatLabelId}
              disabled={locked}
              dropUp
            />
          </div>
          {repeat !== 'none' && (
            <Field
              className={styles.interval}
              label={t('equipment.detail.maintenance.dialog.interval')}
              type="number"
              inputMode="numeric"
              min={1}
              max={EQUIPMENT_MAINTENANCE_INTERVAL_MAX}
              disabled={locked}
              value={every}
              onChange={(e) => setEvery(e.target.value)}
              aria-invalid={!intervalValid}
            />
          )}
        </div>
      </div>
    </Modal>
  );
}
